/*
 * File: FreightTermsInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 8:43:17 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetFreightTermsQuery } from 'redux/services/useGladysTheCowService';
import { BlockLoader } from '../BlockLoader';
const { Title } = Typography;

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 10,
    width: '100%'
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

export const FreightTermsInput = (): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);

  /* ****************** Hook API ****************** */
  const { data: freightTermsResponse, isFetching, isLoading } = useGetFreightTermsQuery({ dataAreaId, deliveryTermContains: inputValue, offset });

  /* ****************** JSX / Variables ****************** */
  const freightTermsOptions = useMemo(() => freightTermsResponse?.data?.map((term) => ({ label: term.termsCode, value: term.termsCode })), [freightTermsResponse]);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('freightTerms' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && freightTermsResponse && freightTermsResponse?.totalCount - offset > 25) {
  //     _setOffset((prevState) => prevState + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('freightTerms' as keyof SalesOrderPayloadModel, value);
    _setInputValue(undefined);
  };

  const moreResultsJSX =
    (isLoading || isFetching) && offset ? (
      <div style={{ padding: '0px 10px' }}>
        <Space>
          <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
        </Space>
      </div>
    ) : null;

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.FREIGHT_TERMS}
      </Title>
      <Select
        allowClear
        value={values.freightTerms}
        dropdownRender={dropDownRender}
        // filterOption={false}
        filterOption={(input, option): boolean => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
        loading={isLoading || isFetching}
        onClear={handleClear}
        disabled={isPosted}
        // onPopupScroll={handleScroll}
        // onSearch={handleSearch}
        onSelect={handleSelect}
        options={freightTermsOptions}
        showSearch
        style={styles.formItem}
      />
    </>
  );
};
